
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Key, Shield, Mail, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(8, "Password must be at least 8 characters"),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const emailSchema = z.object({
  newEmail: z.string().email("Invalid email format"),
});

export const SecurityTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showEmailConfirmDialog, setShowEmailConfirmDialog] = useState(false);
  const [newEmailValue, setNewEmailValue] = useState("");
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const passwordForm = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const emailForm = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      newEmail: "",
    },
  });

  const handlePasswordChange = async (values: z.infer<typeof passwordSchema>) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });
      
      if (error) throw error;
      
      toast.success("Password changed successfully");
      passwordForm.reset();
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChangeRequest = (values: z.infer<typeof emailSchema>) => {
    if (values.newEmail === user?.email) {
      toast.error("New email must be different from current email");
      return;
    }
    
    setNewEmailValue(values.newEmail);
    setShowEmailConfirmDialog(true);
  };

  const handleEmailChange = async () => {
    if (!user || !newEmailValue) return;
    
    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmailValue
      });
      
      if (error) throw error;
      
      toast.success("Email change confirmation sent! Please check your new email address.");
      emailForm.reset();
      setShowEmailConfirmDialog(false);
      setNewEmailValue("");
    } catch (error) {
      console.error("Error changing email:", error);
      toast.error("Failed to send email change confirmation");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleTwoFactorToggle = async (checked: boolean) => {
    setLoading(true);
    try {
      // In a real app, we would implement 2FA toggle logic here
      // This is a placeholder for demonstration
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setTwoFactorEnabled(checked);
      toast.success(checked ? "Two-factor authentication enabled" : "Two-factor authentication disabled");
    } catch (error) {
      console.error("Error toggling 2FA:", error);
      toast.error("Failed to update two-factor authentication");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`space-y-${isMobile ? "4" : "6"}`}>
      {/* Email Change Section */}
      <Card>
        <CardHeader className={isMobile ? "pb-3" : ""}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? "text-lg" : ""}`}>
            <Mail className={`${isMobile ? "h-4 w-4" : "h-5 w-5"}`} />
            Change Email
          </CardTitle>
          <CardDescription className={isMobile ? "text-xs" : ""}>
            Update your email address. You will need to confirm the change via email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleEmailChangeRequest)} className={`space-y-${isMobile ? "3" : "4"}`}>
              <div>
                <label className={`block ${isMobile ? "text-sm" : ""} font-medium text-muted-foreground mb-2`}>
                  Current Email
                </label>
                <Input 
                  type="email" 
                  value={user?.email || ""} 
                  readOnly 
                  className={`bg-muted ${isMobile ? "h-9" : ""}`}
                />
              </div>
              
              <FormField
                control={emailForm.control}
                name="newEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={isMobile ? "text-sm" : ""}>New Email</FormLabel>
                    <FormControl>
                      <Input 
                        type="email" 
                        placeholder="Enter new email address"
                        {...field} 
                        className={isMobile ? "h-9" : ""} 
                      />
                    </FormControl>
                    <FormMessage className={isMobile ? "text-xs" : ""} />
                  </FormItem>
                )}
              />
              
              <Button type="submit" disabled={emailLoading} size={isMobile ? "sm" : "default"}>
                {emailLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Request Email Change
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Separator />

      {/* Password Change Section */}
      <Card>
        <CardHeader className={isMobile ? "pb-3" : ""}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? "text-lg" : ""}`}>
            <Key className={`${isMobile ? "h-4 w-4" : "h-5 w-5"}`} />
            {t("change_password")}
          </CardTitle>
          <CardDescription className={isMobile ? "text-xs" : ""}>
            {t("update_password_description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...passwordForm}>
            <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)} className={`space-y-${isMobile ? "3" : "4"}`}>
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={isMobile ? "text-sm" : ""}>{t("current_password")}</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} className={isMobile ? "h-9" : ""} />
                    </FormControl>
                    <FormMessage className={isMobile ? "text-xs" : ""} />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={isMobile ? "text-sm" : ""}>{t("new_password")}</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} className={isMobile ? "h-9" : ""} />
                    </FormControl>
                    <FormMessage className={isMobile ? "text-xs" : ""} />
                  </FormItem>
                )}
              />
              
              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={isMobile ? "text-sm" : ""}>{t("confirm_new_password")}</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} className={isMobile ? "h-9" : ""} />
                    </FormControl>
                    <FormMessage className={isMobile ? "text-xs" : ""} />
                  </FormItem>
                )}
              />
              
              <Button type="submit" disabled={loading} size={isMobile ? "sm" : "default"}>
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("update_password")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Two Factor Authentication Section */}
      <Card>
        <CardHeader className={isMobile ? "pb-3" : ""}>
          <CardTitle className={`flex items-center gap-2 ${isMobile ? "text-lg" : ""}`}>
            <Shield className={`${isMobile ? "h-4 w-4" : "h-5 w-5"}`} />
            {t("two_factor_authentication")}
          </CardTitle>
          <CardDescription className={isMobile ? "text-xs" : ""}>
            {t("two_factor_description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${isMobile ? "text-sm" : ""}`}>{t("enable_2fa")}</p>
              <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>
                {t("require_2fa_code")}
              </p>
            </div>
            <Switch
              checked={twoFactorEnabled}
              onCheckedChange={handleTwoFactorToggle}
              disabled={loading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Email Change Confirmation Dialog */}
      <AlertDialog open={showEmailConfirmDialog} onOpenChange={setShowEmailConfirmDialog}>
        <AlertDialogContent className={isMobile ? "mx-4" : ""}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Email Change</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to change your email address from <strong>{user?.email}</strong> to <strong>{newEmailValue}</strong>.
              <br /><br />
              A confirmation email will be sent to your new email address. You will need to click the link in that email to complete the change.
              <br /><br />
              Until you confirm the new email, you can still log in with your current email address.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isMobile ? "flex-col space-y-2" : ""}>
            <AlertDialogCancel disabled={emailLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEmailChange} disabled={emailLoading}>
              {emailLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Confirmation Email"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
